import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import PublicLayout from '@/layouts/PublicLayout';
import AdminLayout from '@/layouts/AdminLayout';
import Home from '@/pages/Home';
import Inventory from '@/pages/Inventory';
import InventoryDetail from '@/pages/InventoryDetail';
import Compare from '@/pages/Compare';
import Financing from '@/pages/Financing';
import About from '@/pages/About';
import Login from '@/pages/Login';
import AdminDashboard from '@/pages/admin/Dashboard';
import InventoryManagement from '@/pages/admin/InventoryManagement';
import AdminLeads from '@/pages/admin/Leads';
import AdminClients from '@/pages/admin/Clients';
import AdminDeals from '@/pages/admin/Deals';
import AdminStaff from '@/pages/admin/Staff';
import AdminSettings from '@/pages/admin/Settings';
import AdminFinancing from '@/pages/admin/Financing';
import AdminCMS from '@/pages/admin/CMS';
import AdminDocuments from '@/pages/admin/Documents';
import AdminLenders from '@/pages/admin/Lenders';
import AdminNotifications from '@/pages/admin/Notifications';
import ClientDashboard from '@/pages/client/ClientDashboard';
import ClientPortfolio from '@/pages/admin/ClientPortfolio';
import PublicUpload from '@/pages/PublicUpload';
import SpecSheet from '@/pages/SpecSheet';
import InviteRegister from '@/pages/InviteRegister';
import PortalRegisterRedirect from '@/pages/PortalRegisterRedirect';
import ResetPassword from '@/pages/ResetPassword';
import EmailTemplates from '@/pages/admin/EmailTemplates';
import Consignment from '@/pages/admin/Consignment';
import SocialMedia from '@/pages/admin/SocialMedia';
import Prospects from '@/pages/admin/Prospects';
import DealDetail from '@/pages/admin/DealDetail';
import ApplicationDetail from '@/pages/admin/ApplicationDetail';
import ESignPage from '@/pages/ESignPage';
import CreditAppTemplate from '@/pages/admin/CreditAppTemplate';
import DocTemplates from '@/pages/admin/DocTemplates';
import TemplateBuilder from '@/pages/admin/TemplateBuilder';
import AuditReport from '@/pages/admin/AuditReport';
import DocSignPage from '@/pages/DocSignPage';
import ImpersonatePage from '@/pages/ImpersonatePage';
import PageLock from '@/components/PageLock';

function ProtectedRoute({ children, requireStaff, requireAdmin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (requireStaff && user.user_type !== 'staff') return <Navigate to="/" />;
  if (requireAdmin && user.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function ClientRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/inventory/:id" element={<InventoryDetail />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/financing" element={<Financing />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/portal" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
            <Route path="/upload/:token" element={<PublicUpload />} />
            <Route path="/register/:token" element={<InviteRegister />} />
            <Route path="/portal/register" element={<PortalRegisterRedirect />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Route>

          {/* Standalone pages (no layout) */}
          <Route path="/inventory/:id/print" element={<SpecSheet />} />
          <Route path="/esign/:token" element={<ESignPage />} />
          <Route path="/sign/:token" element={<DocSignPage />} />
          <Route path="/impersonate" element={<ImpersonatePage />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute requireStaff><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="inventory" element={<InventoryManagement />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="clients/:clientId" element={<ClientPortfolio />} />
            <Route path="clients/:clientId/deals/:dealId" element={<DealDetail />} />
            <Route path="credit-app-template" element={<CreditAppTemplate />} />
            <Route path="doc-templates" element={<DocTemplates />} />
            <Route path="doc-templates/:templateId" element={<TemplateBuilder />} />
            <Route path="audit/:token" element={<AuditReport />} />
            <Route path="deals" element={<PageLock pageKey="deals" label="Applications"><AdminDeals /></PageLock>} />
            <Route path="deals/:dealId" element={<ApplicationDetail />} />
            <Route path="financing" element={<PageLock pageKey="financing" label="Financing Requests"><AdminFinancing /></PageLock>} />
            <Route path="documents" element={<PageLock pageKey="documents" label="Documents"><AdminDocuments /></PageLock>} />
            <Route path="lenders" element={<PageLock pageKey="lenders" label="Lenders"><AdminLenders /></PageLock>} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="staff" element={<PageLock pageKey="system.staff" label="Staff"><AdminStaff /></PageLock>} />
            <Route path="settings" element={<PageLock pageKey="settings" label="Settings"><AdminSettings /></PageLock>} />
            <Route path="cms" element={<PageLock pageKey="system.cms" label="CMS"><AdminCMS /></PageLock>} />
            <Route path="email-templates" element={<PageLock pageKey="system.email_templates" label="Email Templates"><EmailTemplates /></PageLock>} />
            <Route path="consignment" element={<PageLock pageKey="inventory.consignment" label="Consignment"><Consignment /></PageLock>} />
            <Route path="social-media" element={<SocialMedia />} />
            <Route path="prospects" element={<PageLock pageKey="clients.prospects" label="Prospects"><Prospects /></PageLock>} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
